/**
 * AttributeSeriesChart — a small numeric value-history line for one
 * twin attribute (#1913 C), following the ``BalanceChart`` pattern:
 * recharts does NOT render under jsdom, so the data shaping lives in the
 * pure ``attributeHistory`` helpers and the component only wires the
 * chart (tested for the wrapper + empty state, not SVG internals).
 *
 * PINNED DEFAULT (#1913): these are WRITE-TIME points. After a one-day
 * historical backfill every point shares that write day, so the x-axis
 * clusters — understood and accepted; the point tooltips carry the value
 * meaning, and multi-year DOMAIN axes stay the job of year-keyed facts.
 */
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatDateString } from '../formatValue';
import type { ChartPoint } from './attributeHistory';
import './AttributeSeriesChart.css';

/**
 * The tooltip label for one point: the timezone-safe UTC calendar date
 * (via the shared ISO-prefix ``formatDateString`` — NOT a local
 * ``new Date().toLocaleDateString``, which drifts a late-UTC ``valid_from``
 * a day for the DE audience), plus its "source doc → year" provenance
 * where known. Exported so the date/provenance shaping is unit-testable
 * without rendering recharts (which needs no jsdom width).
 */
export function seriesTooltipLabel(iso: string, provenance: string | null): string {
  const dateStr = formatDateString(iso);
  return provenance ? `${dateStr} · ${provenance}` : dateStr;
}

export interface AttributeSeriesChartProps {
  data: ChartPoint[];
  label: string;
  /** Renders a value for the tooltip (unit-aware); defaults to de-DE number. */
  valueFormatter?: (value: number) => string;
}

export default function AttributeSeriesChart({ data, label, valueFormatter }: AttributeSeriesChartProps) {
  // ``data`` may carry ``null`` gaps (removed / non-numeric points); the
  // chart is empty only when NO point has a numeric value.
  if (!data.some((p) => p.value != null)) {
    return <p className="attr-series__empty">Kein Werteverlauf vorhanden.</p>;
  }
  const fmt = valueFormatter ?? ((v: number) => v.toLocaleString('de-DE'));

  return (
    <div className="attr-series" role="img" aria-label={`Werteverlauf: ${label}`}>
      <ResponsiveContainer width="100%" height={160}>
        <LineChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
          {/* Axis + tooltip both use the timezone-safe ISO-prefix formatter
              so a late-UTC valid_from matches the list/log (never drifts a
              day for the UTC+1/+2 audience). */}
          <XAxis dataKey="t" tickFormatter={formatDateString} tick={{ fontSize: 11 }} minTickGap={24} />
          <YAxis tickFormatter={(v: number) => fmt(v)} tick={{ fontSize: 11 }} width={52} />
          <Tooltip
            formatter={(v: number) => [fmt(v), label]}
            labelFormatter={(iso: string, payload?: ReadonlyArray<{ payload?: ChartPoint }>) => {
              const prov = payload && payload.length > 0 ? (payload[0]?.payload?.provenance ?? null) : null;
              return seriesTooltipLabel(iso, prov);
            }}
          />
          <Line
            type="monotone"
            dataKey="value"
            name={label}
            stroke="#2563eb"
            strokeWidth={2}
            dot={{ r: 3 }}
            // A ``null`` value (removed / non-numeric point) breaks the
            // line so it never implies the value persisted across a gap.
            connectNulls={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
